import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { DownloadIcon, LayoutGridIcon, Loader2Icon, XIcon } from 'lucide-react';
import { slides, type DeckSlide } from '../../deckSlides';
import { STORYBOARD_FILE_NAME, buildStoryboardPdf, triggerDownload } from '../../utils/exportStoryboard';
import { t } from './primitives';
import { SlidePreview } from './SlidePreview';

/** The storyboard slide itself is excluded so it never previews itself. */
const STORYBOARD_SLIDE_ID = 'scr_stb012';
type StoryboardButtonProps = {
  /** 'light' sits on dark surfaces, 'onAccent' sits on the purple panel. */
  tone?: 'light' | 'onAccent';
  label?: string;
};
export function StoryboardButton({
  tone = 'light',
  label = 'View storyboard'
}: StoryboardButtonProps) {
  const [open, setOpen] = useState(false);
  return <>
      <button type="button" onClick={() => setOpen(true)} className={`${t.label} relative z-20 inline-flex cursor-pointer items-center gap-[0.7vw] rounded-full px-[1.3vw] py-[1vh] font-semibold uppercase tracking-[0.18em] transition-colors ${tone === 'onAccent' ? 'bg-white text-[#5B1FB8] hover:bg-white/90' : 'border border-[#7D38E5] bg-[#7D38E5]/20 text-white hover:bg-[#7D38E5]/35'}`} data-id="element-290" data-mp="3dnpejhwe4gnh">
        <LayoutGridIcon aria-hidden className="h-[clamp(9px,1.1vw,22px)] w-[clamp(9px,1.1vw,22px)]" data-id="element-291" data-mp="2yvd3lgd6x82u" />
        {label}
      </button>
      {open ? <Storyboard pages={(slides ?? []).filter(slide => slide.id !== STORYBOARD_SLIDE_ID)} onClose={() => setOpen(false)} data-id="element-292" data-mp="2p63b3zefpne7" /> : null}
    </>;
}

/** Renders every slide off-screen and saves them as one downloaded PDF file. */
export function DownloadStoryboardButton({
  label = 'Download PDF',
  pages
}: {
  label?: string;
  pages?: DeckSlide[];
}) {
  const [progress, setProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const exportPages = pages ?? (slides ?? []).filter(slide => slide.id !== STORYBOARD_SLIDE_ID);
  const busy = progress !== null;
  const onClick = async () => {
    if (busy) return;
    setError(null);
    setFileUrl(null);
    setProgress({
      done: 0,
      total: exportPages.length
    });
    try {
      const blob = await buildStoryboardPdf(exportPages, (done, total) => setProgress({
        done,
        total
      }));
      const url = URL.createObjectURL(blob);
      setFileUrl(url);
      // Programmatic saves are blocked in some sandboxes — the link below is the
      // guaranteed path, so a failure here is not fatal.
      try {
        triggerDownload(url, STORYBOARD_FILE_NAME);
      } catch {
        /* fall back to the visible save link */
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'PDF export failed');
    } finally {
      setProgress(null);
    }
  };
  return <span className="relative z-20 inline-flex items-center gap-[0.6vw]" data-id="element-293" data-mp="rjf6xg8sk43x">
      <button type="button" onClick={onClick} disabled={busy} className={`${t.label} inline-flex cursor-pointer items-center gap-[0.6vw] rounded-full bg-[#7D38E5] px-[1.3vw] py-[1vh] font-semibold uppercase tracking-[0.18em] text-white transition-colors hover:bg-[#6a2ecb] disabled:cursor-wait disabled:opacity-80`} data-id="element-294" data-mp="mldkqmg2joo8">
        {busy ? <Loader2Icon aria-hidden className="h-[clamp(9px,1.1vw,22px)] w-[clamp(9px,1.1vw,22px)] animate-spin" data-id="element-295" data-mp="3o2n16a353aw" /> : <DownloadIcon aria-hidden className="h-[clamp(9px,1.1vw,22px)] w-[clamp(9px,1.1vw,22px)]" data-id="element-296" data-mp="ii77uvygaz8s" />}
        {busy ? `Building PDF · ${progress?.done ?? 0}/${progress?.total ?? 0}` : label}
      </button>

      {fileUrl ? <a href={fileUrl} download={STORYBOARD_FILE_NAME} target="_blank" rel="noopener noreferrer" className={`${t.label} inline-flex items-center gap-[0.5vw] rounded-full bg-white px-[1.1vw] py-[1vh] font-semibold uppercase tracking-[0.18em] text-[#5B1FB8] hover:bg-white/90`} data-id="element-297" data-mp="1p2q8inee4hkz">
          <DownloadIcon aria-hidden className="h-[clamp(9px,1.1vw,22px)] w-[clamp(9px,1.1vw,22px)]" data-id="element-298" data-mp="cjt52vymioid" />
          Save PDF
        </a> : null}

      {error ? <span className={`${t.label} max-w-[16vw] rounded-full bg-red-500/15 px-[1vw] py-[0.8vh] leading-[1.3] text-red-300`} data-id="element-299" data-mp="mgxowp4zy7w0">
          {error}
        </span> : null}
    </span>;
}
type StoryboardProps = {
  pages: DeckSlide[];
  onClose: () => void;
};
function Storyboard({
  pages,
  onClose
}: StoryboardProps) {
  // Swallow the deck harness' navigation keys while the storyboard is open.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (['ArrowRight', 'ArrowLeft', ' ', 'PageUp', 'PageDown'].includes(event.key)) {
        event.stopPropagation();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);
  if (typeof document === 'undefined') return null;
  return createPortal(<section role="dialog" aria-modal="true" aria-label="Deck storyboard" className="mp-storyboard fixed inset-0 z-[999] overflow-y-auto bg-[#F2F0F6] px-[3vw] py-[3vh] text-[#12101A]" data-id="element-300" data-mp="1y47urvf3uvog">
      <header className="mp-storyboard-chrome mb-[3vh] flex items-end justify-between gap-[2vw]" data-id="element-301" data-mp="1kb8i016pjfkl">
        <div data-id="element-302" data-mp="2gsmm6jtc4bps">
          <p className={`${t.label} font-semibold uppercase tracking-[0.24em] text-[#5B1FB8]`} data-id="element-303" data-mp="2gtela4m54rkt">
            Storyboard · {pages.length} pages
          </p>
          <h2 className={`${t.title} mt-[1vh] font-semibold leading-[1.05] tracking-[-0.02em]`} data-id="element-304" data-mp="a4m7sq913j4w">
            Snakker for LEO Pharma
          </h2>
        </div>
        <div className="flex shrink-0 items-center gap-[0.8vw]" data-id="element-305" data-mp="f5jqr51v4h7g">
          <DownloadStoryboardButton pages={pages} data-id="element-306" data-mp="e1f1jg1d8tw" />
          <button type="button" onClick={onClose} aria-label="Close storyboard" className={`${t.label} inline-flex cursor-pointer items-center gap-[0.6vw] rounded-full border border-black/15 px-[1.3vw] py-[1vh] font-semibold uppercase tracking-[0.18em] text-black/60 transition-colors hover:bg-black/5`} data-id="element-307" data-mp="5dmdztv7hh6m">
            <XIcon aria-hidden className="h-[clamp(9px,1.1vw,22px)] w-[clamp(9px,1.1vw,22px)]" data-id="element-308" data-mp="3dn5s96lgtkp5" />
            Close
          </button>
        </div>
      </header>

      <ol className="mp-storyboard-grid grid grid-cols-3 gap-x-[2vw] gap-y-[3vh]" data-id="element-309" data-mp="1burgyqdffehp">
        {pages.map((slide, index) => <li key={slide.id} className="mp-storyboard-page" data-id="element-310" data-mp="31b3g2v11wnqn" data-mpi={index}>
            <div className="overflow-hidden rounded-[0.4vw] border border-black/10 bg-white p-[0.35vw] shadow-sm" data-id="element-311" data-mp="31bmuw2vemw0g">
              <SlidePreview screenId={slide.id} title={slide.title} data-id="element-312" data-mp="2w9xlssrqssf0" />
            </div>
            <p className={`${t.label} mp-storyboard-caption mt-[1.2vh] flex gap-[0.6vw] leading-[1.3] text-black/60`} data-id="element-313" data-mp="3b27sbmzcduhs">
              <span className="font-semibold text-[#5B1FB8]" data-id="element-314" data-mp="3k11xw2unhv7h">
                {String(index + 1).padStart(2, '0')}
              </span>
              <span data-id="element-315" data-mp="3fz7zy2bub5b0">{slide.title}</span>
            </p>
          </li>)}
      </ol>

      <p className={`${t.label} mp-storyboard-chrome mt-[3vh] border-t border-black/10 pt-[2vh] text-black/45`} data-id="element-316" data-mp="3typqam4saqmh">
        Download PDF renders every slide and saves a landscape,
        one-slide-per-page file to your downloads — it takes a few seconds.
      </p>
    </section>, document.body);
}